/**
 * Recommendation categories: the contract shared by the judge, the metrics
 * service, the API routes and the dashboard.
 *
 * For every discovery answer on a daily run, an LLM judge classifies EVERY
 * brand the answer names (tracked or not) as a top choice, one of many, a
 * generic mention or cautioned against, with best-for phrases, caveats and a
 * verbatim evidence quote (`BrandMentionAssessment` / `BrandMentionCaveat`).
 * This file holds the vocabulary, the UI labels, the filters and every
 * response shape. The shapes mirror the approved design-lab selectors
 * (`apps/web/modules/design/lib/recommendation-fixtures.ts`) with lab-only
 * fields dropped, so the UI swap from fixtures to hooks is mechanical.
 *
 * Two metric families, which every surface keeps visibly apart:
 *  - RATES, per brand per answer: denominator = answers in the slice.
 *  - SHARES, across all brands (the market): denominator = every brand's
 *    recommendations (or top picks) in the slice, untracked brands included,
 *    so a column of shares sums to 100.
 *
 * Pure types and constants: safe for `@repo/core/client`.
 */

import type { PlatformKey } from "../utils/funnel-breakdown";
import type { PersonaLensGroupBy } from "./persona-lens";
import type { FunnelStage } from "./prompt-coverage";

/* ------------------------------------------------------------------ */
/* Categories                                                          */
/* ------------------------------------------------------------------ */

/** Judge output keys, strongest first. This order is the stacking order. */
export const RECOMMENDATION_CATEGORIES = [
	"top_choice",
	"one_of_many",
	"generic_mention",
	"cautioned_against",
] as const;

export type RecommendationCategory = (typeof RECOMMENDATION_CATEGORIES)[number];

export function isRecommendationCategory(
	value: unknown,
): value is RecommendationCategory {
	return (
		typeof value === "string" &&
		(RECOMMENDATION_CATEGORIES as readonly string[]).includes(value)
	);
}

/** The recommended family: a brand counts as recommended in these two. */
export const RECOMMENDED_CATEGORIES: readonly RecommendationCategory[] = [
	"top_choice",
	"one_of_many",
];

/**
 * Per-answer rate keys: the four categories plus "not_mentioned", so the
 * rates for one brand sum to 100% of answers (with `unclassified`, below).
 */
export const RECOMMENDATION_RATE_KEYS = [
	...RECOMMENDATION_CATEGORIES,
	"not_mentioned",
] as const;

export type RecommendationRateKey = (typeof RECOMMENDATION_RATE_KEYS)[number];

/**
 * How one answer treats one brand, as an L3 row shows it:
 *  - a category;
 *  - "not_mentioned": the answer does not name the brand;
 *  - "unclassified": the unified matcher found a tracked brand in the answer
 *    but the judge left it out (stored as `category = NULL`). Rare; its rate
 *    is a judge-quality signal.
 */
export type RecommendationRowCategory = RecommendationRateKey | "unclassified";

export const RECOMMENDATION_ROW_CATEGORIES = [
	...RECOMMENDATION_RATE_KEYS,
	"unclassified",
] as const satisfies readonly RecommendationRowCategory[];

/** UI labels (Jim's names, approved at design review). */
export const RECOMMENDATION_CATEGORY_LABELS: Record<
	RecommendationRowCategory,
	string
> = {
	top_choice: "Top choice",
	one_of_many: "One of many",
	generic_mention: "Generic mention",
	cautioned_against: "Cautioned against",
	not_mentioned: "Not mentioned",
	unclassified: "Unclassified",
};

/** One sentence each, for HoverCard explainers. */
export const RECOMMENDATION_CATEGORY_DEFINITIONS: Record<
	RecommendationRowCategory,
	string
> = {
	top_choice:
		"The answer singles the brand out as its pick or its explicit top recommendation.",
	one_of_many:
		"The answer recommends the brand alongside others, often with a note on who it is best for.",
	generic_mention:
		"The answer names the brand without evaluating or recommending it.",
	cautioned_against:
		"The answer steers this asker away from the brand, usually for a stated reason such as price or complexity.",
	not_mentioned: "The answer does not name the brand at all.",
	unclassified:
		"The answer names the brand, but the classifier did not rate it.",
};

/* ------------------------------------------------------------------ */
/* Caveat reasons                                                      */
/* ------------------------------------------------------------------ */

export const RECOMMENDATION_REASON_TYPES = [
	"price",
	"complexity",
	"company_size_fit",
	"missing_capability",
	"integration",
	"support_service",
	"reliability_quality",
	"security_compliance",
	"reputation",
	"other",
] as const;

export type RecommendationReasonType =
	(typeof RECOMMENDATION_REASON_TYPES)[number];

export function isRecommendationReasonType(
	value: unknown,
): value is RecommendationReasonType {
	return (
		typeof value === "string" &&
		(RECOMMENDATION_REASON_TYPES as readonly string[]).includes(value)
	);
}

export const RECOMMENDATION_REASON_TYPE_LABELS: Record<
	RecommendationReasonType,
	string
> = {
	price: "Price",
	complexity: "Complexity",
	company_size_fit: "Company size fit",
	missing_capability: "Missing capability",
	integration: "Integrations",
	support_service: "Support and service",
	reliability_quality: "Reliability and quality",
	security_compliance: "Security and compliance",
	reputation: "Reputation",
	other: "Other",
};

/**
 * Stable id of one grouped caveat phrase: `${reasonType}:${reasonNorm}`
 * (`reasonNorm` = `normalizePhrase(reason)`). It is what the L2 links to L3
 * with (`reason=` query param) and what `topCautionReason.reasonId` carries.
 */
export function recommendationReasonId(
	reasonType: RecommendationReasonType,
	reasonNorm: string,
): string {
	return `${reasonType}:${reasonNorm}`;
}

/** Split a reason id back into its parts; null when malformed. */
export function parseRecommendationReasonId(
	reasonId: string,
): { reasonType: RecommendationReasonType; reasonNorm: string } | null {
	const at = reasonId.indexOf(":");
	if (at <= 0) {
		return null;
	}
	const reasonType = reasonId.slice(0, at);
	const reasonNorm = reasonId.slice(at + 1);
	if (!isRecommendationReasonType(reasonType) || !reasonNorm) {
		return null;
	}
	return { reasonType, reasonNorm };
}

/* ------------------------------------------------------------------ */
/* Windows and thresholds                                              */
/* ------------------------------------------------------------------ */

/** Same presets as the brand-dashboard drill-down window standard. */
export const RECOMMENDATION_WINDOW_DAYS = [7, 30, 90] as const;
export type RecommendationWindowDays =
	(typeof RECOMMENDATION_WINDOW_DAYS)[number];
/** The API default when `days` is omitted (the drill-down standard). */
export const RECOMMENDATION_DEFAULT_WINDOW_DAYS: RecommendationWindowDays = 7;

/** Below this many answers a slice is an "early read". */
export const RECOMMENDATION_EARLY_READ_MIN_ANSWERS = 30;

/** Key of the leaderboard's folded tail row (never a real brandKey). */
export const RECOMMENDATION_OTHER_KEY = "__other__";

/** Default number of non-focal brands shown before the tail folds. */
export const RECOMMENDATION_LEADERBOARD_TOP_N = 8;

/* ------------------------------------------------------------------ */
/* Filters                                                             */
/* ------------------------------------------------------------------ */

/**
 * One slice of answers. Every filter narrows numerator AND denominator
 * together, and an empty slice is empty (never the unfiltered numbers).
 */
export interface RecommendationFilters {
	/**
	 * 7 | 30 | 90, ending today (UTC dispatch days, inclusive). Default
	 * RECOMMENDATION_DEFAULT_WINDOW_DAYS (7).
	 */
	windowDays?: RecommendationWindowDays;
	/** One tracked platform; null/undefined = all. */
	platform?: PlatformKey | null;
	/** Funnel stage of the prompt (`funnelStageFor(queryType)`). */
	stage?: FunnelStage | null;
	/** Restrict to one project's prompts. */
	projectId?: string | null;
	/** Prompt tags; a prompt matches when it carries ANY of them. */
	tags?: string[] | null;
	/**
	 * A LOCATION child's runs. null/undefined = brand-wide (parent-scope runs
	 * only, `locationPropertyId IS NULL`), the dashboard's headline basis.
	 */
	locationPropertyId?: string | null;
	/**
	 * One persona's answers (runType "nightly_persona") instead of the
	 * baseline. null/undefined = the baseline (no persona), exactly as before
	 * personas existed. A persona of another property is an empty slice.
	 */
	personaId?: string | null;
	/**
	 * Every non-archived persona of the property sharing one attribute value
	 * (e.g. `{ groupBy: "roleGroup", value: "finance" }`). Ignored when
	 * `personaId` is set. `groupBy: "persona"` means `value` is a persona id.
	 */
	personaGroup?: RecommendationPersonaGroup | null;
}

/** A group of personas sharing one attribute value. */
export interface RecommendationPersonaGroup {
	groupBy: PersonaLensGroupBy;
	/**
	 * The attribute value, as `personaLensGroupKey` returns it
	 * (PERSONA_LENS_UNSET_GROUP = no value set; PERSONA_LENS_PEOPLE_GROUP,
	 * optionally `:segment`, = individuals).
	 */
	value: string;
}

/** The filters as the service applied them (echoed in every response). */
export interface RecommendationAppliedFilters {
	windowDays: RecommendationWindowDays;
	platform: PlatformKey | null;
	stage: FunnelStage | null;
	projectId: string | null;
	tags: string[];
	locationPropertyId: string | null;
	personaId: string | null;
	/** Present only when a persona group was requested (and no personaId). */
	personaGroup?: RecommendationPersonaGroup;
}

/** Options for the L2 payload beyond the slice itself. */
export interface RecommendationOptions {
	/**
	 * Non-focal brands shown before the tail folds into "Other". The focal
	 * brand is always shown. null = show every brand. Default
	 * RECOMMENDATION_LEADERBOARD_TOP_N.
	 */
	topN?: number | null;
	/**
	 * Brand whose rates the trend plots. Default: the focal brand (BRAND
	 * subject) or the leaderboard leader (CATEGORY).
	 */
	trendBrandKey?: string | null;
	/** Default: "day" for a 7-day window, "week" for 30 and 90. */
	trendGranularity?: "day" | "week" | null;
}

/* ------------------------------------------------------------------ */
/* Summary (focal brand, rate family)                                  */
/* ------------------------------------------------------------------ */

/** Percent of answers, 0 to 100 unrounded; null when there are no answers. */
export interface RecommendationRates {
	topChoice: number | null;
	oneOfMany: number | null;
	/** top_choice + one_of_many. */
	recommended: number | null;
	generic: number | null;
	cautioned: number | null;
	notMentioned: number | null;
	/** Named but not rated by the judge (see RecommendationRowCategory). */
	unclassified: number | null;
	/** Named in any way (100 - notMentioned). */
	mentioned: number | null;
}

/** Answers by how they treat one brand. Sums to the slice's answer count. */
export type RecommendationCounts = Record<RecommendationRowCategory, number>;

export interface RecommendationLadder {
	/** Every answer in the slice. */
	answers: number;
	/** Answers citing one of the focal brand's own pages. */
	cited: number;
	/** Answers naming the brand in any way. */
	mentioned: number;
	/** Top choice + one of many. */
	recommended: number;
	topChoice: number;
}

export interface RecommendationShareFigure {
	/** 0-100 unrounded; null when nobody was recommended in the slice. */
	pct: number | null;
	previousPct: number | null;
	/** Percentage points vs the previous window. */
	delta: number | null;
}

/**
 * Focal-brand rates, ladder and KPI inputs for one slice. BRAND, PRODUCT,
 * PERSON and LOCATION subjects only; null for CATEGORY (no focal brand).
 * Mirrors the lab's `recommendationSummary`.
 */
export interface RecommendationSummary {
	brandKey: string;
	brandName: string;
	windowDays: RecommendationWindowDays;
	/** Answers in the slice (the denominator for every rate). */
	n: number;
	isEmpty: boolean;
	/** 0 < n < RECOMMENDATION_EARLY_READ_MIN_ANSWERS. */
	isEarlyRead: boolean;
	/** Sums to n. */
	counts: RecommendationCounts;
	rates: RecommendationRates;
	/**
	 * The previous equal-length window. Null when history does not reach back
	 * a full window; `n: 0` when it reaches back but nothing ran.
	 */
	previous: { n: number; rates: RecommendationRates } | null;
	/** Percentage-point change vs the previous window. */
	deltas: {
		topChoice: number | null;
		recommended: number | null;
		generic: number | null;
		cautioned: number | null;
		notMentioned: number | null;
	};
	bestForCoverage: {
		/** Share of one-of-many mentions that carry at least one best-for phrase. */
		pct: number | null;
		withBestFor: number;
		oneOfMany: number;
		previousPct: number | null;
		delta: number | null;
	};
	cautioned: { count: number; previousCount: number | null };
	/**
	 * Answers that cite one of the focal brand's own pages but leave it out of
	 * the recommendations (not mentioned, generic mention, cautioned against or
	 * unclassified). Computed from stored citation rows; no LLM.
	 */
	citedNotRecommended: {
		count: number;
		/** Share of the answers that cite the brand. */
		pctOfCited: number | null;
		pctOfAnswers: number | null;
		previousCount: number | null;
	};
	/** Not nested: an answer can mention a brand without citing it. */
	ladder: RecommendationLadder;
	/** The focal brand's market shares (the leaderboard's "you" row). */
	shareOfRecommendation: RecommendationShareFigure;
	topChoiceShare: RecommendationShareFigure;
	/** The most common caution reason in the window, for one-line summaries. */
	topCautionReason: {
		reasonId: string;
		reasonType: RecommendationReasonType;
		reason: string;
		audience: string | null;
		count: number;
		/** Share of the brand's cautions. */
		sharePct: number;
	} | null;
}

/* ------------------------------------------------------------------ */
/* Market overview (every subject; the CATEGORY L1/L2 headline)        */
/* ------------------------------------------------------------------ */

/** Mirrors the lab's `categoryRecommendationOverview`. */
export interface RecommendationMarketOverview {
	windowDays: RecommendationWindowDays;
	n: number;
	isEmpty: boolean;
	isEarlyRead: boolean;
	/** Distinct brands named in the slice. */
	brandsNamed: number;
	/** Mean brands named per answer. */
	avgBrandsPerAnswer: number | null;
	/** Share of answers where AI made a top pick at all. */
	answersWithTopChoicePct: number | null;
	/** Share of answers where AI cautioned against at least one brand. */
	answersWithCautionPct: number | null;
	/** Category mix across every classified mention in the slice. */
	categoryCounts: Record<RecommendationCategory, number>;
	/** Tracked-brand mentions the judge left unrated. */
	unclassified: number;
	/**
	 * The previous equal-length window, same figures. Mirrors
	 * `RecommendationSummary.previous`: null when history does not reach back
	 * a full window; `n: 0` when it reaches but nothing ran in the slice (a
	 * brand-new project, say), which is the signal not to badge anything
	 * "New". Optional for backward compatibility; the service always sets it.
	 */
	previous?: RecommendationMarketOverviewPrevious | null;
	/**
	 * Change vs `previous`: percentage points for the two rates, plain
	 * differences for the counts. Every field is null when `previous` is null
	 * or has no answers. Optional for backward compatibility; the service
	 * always sets it.
	 */
	deltas?: RecommendationMarketOverviewDeltas;
}

/** `RecommendationMarketOverview.previous`: the previous window's tile figures. */
export interface RecommendationMarketOverviewPrevious {
	n: number;
	brandsNamed: number;
	avgBrandsPerAnswer: number | null;
	answersWithTopChoicePct: number | null;
	answersWithCautionPct: number | null;
	categoryCounts: Record<RecommendationCategory, number>;
	unclassified: number;
}

/** `RecommendationMarketOverview.deltas`. */
export interface RecommendationMarketOverviewDeltas {
	/** Percentage points. */
	answersWithTopChoicePct: number | null;
	/** Percentage points. */
	answersWithCautionPct: number | null;
	avgBrandsPerAnswer: number | null;
	brandsNamed: number | null;
}

/* ------------------------------------------------------------------ */
/* Leaderboard (all brands, share family)                              */
/* ------------------------------------------------------------------ */

export interface RecommendationLeaderboardRow {
	/** normalizeBrandKey(name), or RECOMMENDATION_OTHER_KEY for the fold. */
	brandKey: string;
	/** Tracked brands: the canonical tracked name. Untracked: the most common spelling. */
	name: string;
	/** Tracked brands' domain (competitor URL host / the property domain); null otherwise. */
	domain: string | null;
	/** Always false on a CATEGORY subject. */
	isFocal: boolean;
	/** Resolved at READ time: adding a competitor or alias reclassifies history. */
	isTracked: boolean;
	/** The tracked competitor's id (for links); null for focal, untracked and Other. */
	competitorId: string | null;
	isOther: boolean;
	/** Brands folded into "Other" (0 for real rows). */
	foldedCount: number;
	/** 1-based rank by points; null for "Other". */
	rank: number | null;
	/** Recommendations: top choice + one of many, a top choice counted ONCE. */
	points: number;
	/** Share of recommendation, unrounded. All rows sum to 100. */
	sharePct: number;
	/** Largest-remainder rounded to 1 decimal: the column sums to 100.0. */
	sharePctDisplay: number;
	previousSharePct: number | null;
	/** Percentage points vs the previous window. */
	shareDelta: number | null;
	topChoices: number;
	/** The brand's top picks / all top picks in the slice. */
	topChoiceSharePct: number | null;
	topChoiceSharePctDisplay: number | null;
	cautioned: number;
	/** Cautioned answers / answers in the slice (RATE family). Null for Other. */
	cautionedRatePct: number | null;
	/** n: answers naming the brand in any way. */
	mentions: number;
	/** Mentions / answers in the slice. Null for Other. */
	mentionRatePct: number | null;
	categoryCounts: Record<RecommendationCategory, number>;
	/** Named but unrated (tracked brands only). */
	unclassified: number;
}

/** Mirrors the lab's `recommendationLeaderboard`. */
export interface RecommendationLeaderboard {
	subject: "brand" | "category";
	windowDays: RecommendationWindowDays;
	/** Answers in the slice. */
	n: number;
	isEmpty: boolean;
	isEarlyRead: boolean;
	totalPoints: number;
	totalTopChoices: number;
	/** Distinct brands named in the slice. */
	brandsNamed: number;
	rows: RecommendationLeaderboardRow[];
}

/* ------------------------------------------------------------------ */
/* Reasons ("Why AI cautions against you")                             */
/* ------------------------------------------------------------------ */

export interface RecommendationReasonPhrase {
	/** `recommendationReasonId(reasonType, reasonNorm)`. */
	reasonId: string;
	reasonType: RecommendationReasonType;
	/** Display phrase: the most common raw wording in the group. */
	reason: string;
	/** Most common audience for this phrase; null when none was stated. */
	audience: string | null;
	count: number;
	/** Share of all caveats in scope. */
	sharePct: number;
	/** Null when there is no full previous window. */
	previousCount: number | null;
	delta: number | null;
	/**
	 * First seen in the current window with none in the previous window; with
	 * no previous window, first seen in the last 7 days.
	 */
	isNew: boolean;
	/** First UTC date (YYYY-MM-DD) this phrase appears for this scope. */
	firstSeen: string;
}

export interface RecommendationReasonGroup {
	reasonType: RecommendationReasonType;
	label: string;
	count: number;
	sharePct: number;
	previousCount: number | null;
	delta: number | null;
	/** True when every phrase in the group is new. */
	isNew: boolean;
	phrases: RecommendationReasonPhrase[];
}

/** Mirrors the lab's `recommendationReasons`. */
export interface RecommendationReasons {
	/** The focal brand (BRAND subject) or null = every brand (CATEGORY). */
	brandKey: string | null;
	/**
	 * "cautioned" counts caveats on cautioned-against mentions only (the L2
	 * section). "all" also counts caveats on positive categories.
	 */
	scope: "cautioned" | "all";
	windowDays: RecommendationWindowDays;
	/** Caveats counted (a mention can carry several). */
	total: number;
	previousTotal: number | null;
	/** Mentions in the window that were cautioned against. */
	cautionedMentions: number;
	/** Sorted by count, largest first. Empty = the "no cautions" state. */
	groups: RecommendationReasonGroup[];
}

/* ------------------------------------------------------------------ */
/* Best-for phrases ("Where AI picks you")                             */
/* ------------------------------------------------------------------ */

export interface RecommendationBestForRow {
	/** Display phrase: the most common raw wording in the group. */
	phrase: string;
	/** normalizePhrase(phrase): the grouping key and the L3 `bestFor` filter value. */
	phraseNorm: string;
	count: number;
	/** Share of the brand's best-for phrases in the window. */
	sharePct: number;
	/** How many came with a top-choice verdict. */
	topChoiceCount: number;
	previousCount: number | null;
	delta: number | null;
	isNew: boolean;
}

/** Mirrors the lab's `bestForPhrases`. BRAND subjects only (null for CATEGORY). */
export interface RecommendationBestFor {
	brandKey: string;
	windowDays: RecommendationWindowDays;
	total: number;
	rows: RecommendationBestForRow[];
}

/* ------------------------------------------------------------------ */
/* Themes (cautions and best-for, grouped by meaning)                  */
/* ------------------------------------------------------------------ */

/**
 * The judge writes caution reasons and best-for notes in its own words, so
 * one concern arrives in many wordings. A THEME groups those wordings by
 * meaning, per property: written by the utility model from the property's
 * own phrases (never a fixed software-buyer taxonomy) and stored, so a theme
 * keeps its name from one period to the next. Phrases map to a theme by
 * their normalized key (`reasonNorm` for cautions, `normalizePhrase` for
 * best-for notes); the stored assessments are never rewritten.
 */
export const RECOMMENDATION_THEME_KINDS = ["caution", "best_for"] as const;
export type RecommendationThemeKind =
	(typeof RECOMMENDATION_THEME_KINDS)[number];

export function isRecommendationThemeKind(
	value: unknown,
): value is RecommendationThemeKind {
	return (
		typeof value === "string" &&
		(RECOMMENDATION_THEME_KINDS as readonly string[]).includes(value)
	);
}

/** Themes listed before the rest fold behind "Show more" on the L2. */
export const RECOMMENDATION_THEMES_VISIBLE = 6;

/** One wording inside a theme. */
export interface RecommendationThemePhrase {
	/** Display phrase: the most common raw wording for this key. */
	phrase: string;
	/** The grouping key, and the L3 `themePhrase` filter value. */
	phraseNorm: string;
	/** Times this wording was given in the window. */
	count: number;
	/** Most common audience (cautions only); null when none was stated. */
	audience: string | null;
	/** Absent from the previous window (false when there is none). */
	isNew: boolean;
}

/** A brand a caution theme was raised about (CATEGORY subjects). */
export interface RecommendationThemeBrand {
	brandKey: string;
	name: string;
	/** Cautioned mentions of this brand that raised the theme. */
	count: number;
}

export interface RecommendationTheme {
	id: string;
	label: string;
	description: string;
	/**
	 * Distinct cautioned mentions (cautions) or recommendations (best for)
	 * that raised the theme, never phrases: two reasons in one answer that
	 * fall in the same theme count once.
	 */
	count: number;
	/** Null when there is no full previous window. */
	previousCount: number | null;
	/** Best for only: how many of `count` were the top choice. */
	topChoiceCount: number | null;
	/** Raised in this window and not in the previous one (needs a previous window). */
	isNew: boolean;
	/** CATEGORY cautions: the brands it was raised about, largest first (top 2). */
	brands: RecommendationThemeBrand[] | null;
	/** CATEGORY cautions: brands beyond `brands`, folded. */
	otherBrands: { brands: number; count: number } | null;
	/** Largest first. */
	phrases: RecommendationThemePhrase[];
}

export interface RecommendationThemeSet {
	kind: RecommendationThemeKind;
	/**
	 * The denominator: cautioned mentions (the focal brand's on a BRAND
	 * property, every brand's on a CATEGORY one) or the focal brand's
	 * recommendations with a best-for note.
	 */
	base: number;
	previousBase: number | null;
	/** Most mentions first, then most top choices, then label. */
	themes: RecommendationTheme[];
	/**
	 * Wordings in the window with no theme yet (grouping runs after the
	 * answers are judged). Their mentions already count in `base`.
	 */
	pending: RecommendationThemePhrase[];
}

/* ------------------------------------------------------------------ */
/* Trend (one chart)                                                   */
/* ------------------------------------------------------------------ */

export interface RecommendationTrendPoint {
	/** First UTC day of the bucket (YYYY-MM-DD). */
	date: string;
	/** Answers in the bucket. */
	n: number;
	topChoiceRate: number | null;
	cautionedRate: number | null;
	recommendedRate: number | null;
	mentionRate: number | null;
}

/** Mirrors the lab's `recommendationTrend`. */
export interface RecommendationTrend {
	/** Null only when a CATEGORY slice named no brand at all. */
	brandKey: string | null;
	brandName: string | null;
	granularity: "day" | "week";
	/**
	 * Oldest first. Weeks are 7-day buckets ending on the window's last day,
	 * so the first bucket of a 30/90-day view can be short (its n says so).
	 */
	points: RecommendationTrendPoint[];
}

/* ------------------------------------------------------------------ */
/* The L2 payload                                                      */
/* ------------------------------------------------------------------ */

export interface RecommendationsResponse {
	/** "category" = no focal brand: summary and bestFor are null, no "you" row. */
	subject: "brand" | "category";
	hasFocalSubject: boolean;
	/** The focal brand as the leaderboard keys it; null for CATEGORY. */
	focal: { brandKey: string; name: string; domain: string | null } | null;
	filters: RecommendationAppliedFilters;
	/**
	 * False when the property has no assessed answer at all, ignoring every
	 * filter: the "daily tracking is not set up yet" state, as opposed to an
	 * empty slice (n = 0 with this true).
	 */
	hasAssessedAnswers: boolean;
	summary: RecommendationSummary | null;
	overview: RecommendationMarketOverview;
	leaderboard: RecommendationLeaderboard;
	reasons: RecommendationReasons;
	bestFor: RecommendationBestFor | null;
	/** "Why AI cautions against you": the reasons, grouped into themes. */
	cautionThemes: RecommendationThemeSet;
	/** "Where AI picks you": best-for notes in themes. Null for CATEGORY. */
	bestForThemes: RecommendationThemeSet | null;
	trend: RecommendationTrend;
}

/* ------------------------------------------------------------------ */
/* L3: answers                                                         */
/* ------------------------------------------------------------------ */

/** One caveat as the judge stored it (separate from the category). */
export interface RecommendationCaveat {
	reasonId: string;
	reasonType: RecommendationReasonType;
	reason: string;
	audience: string | null;
}

/** Row filters on top of the slice. */
export interface RecommendationAnswerRowFilters {
	/** One brand's rows; omit for every brand's rows. */
	brandKey?: string | null;
	/**
	 * "not_mentioned" lists answers where `brandKey` (default: the focal
	 * brand) is absent: one row per answer, category "not_mentioned".
	 */
	category?: RecommendationRowCategory | null;
	reasonType?: RecommendationReasonType | null;
	/** `recommendationReasonId(...)`. */
	reasonId?: string | null;
	/** A best-for phrase; matched on normalizePhrase. */
	bestFor?: string | null;
	/**
	 * A theme id: caution themes match rows with a caveat in the theme
	 * (cautioned-against rows only); best-for themes match rows with a
	 * best-for note in the theme.
	 */
	themeId?: string | null;
	/** Narrows `themeId` to one wording (its phraseNorm). */
	themePhrase?: string | null;
	/**
	 * Answers citing the focal brand's pages that leave it out of the
	 * recommendations: one focal row per answer (including not-mentioned
	 * rows). Overrides brandKey/category/reason/bestFor.
	 */
	citedNotRecommended?: boolean;
}

/** One (answer x brand) row, newest first. Mirrors the lab's `RecAnswerRow`. */
export interface RecommendationAnswerRow {
	/** `${executionId}.${brandKey}`: stable row id. */
	id: string;
	/** Opens the answer record (`GET /properties/:id/answers/:executionId`). */
	executionId: string;
	/** UTC dispatch day, YYYY-MM-DD. */
	date: string;
	/** Raw execution platform ("openai", "google_ai_overview", ...). */
	platform: string;
	/** Normalized platform key; null for an untracked platform. */
	platformKey: PlatformKey | null;
	queryId: string;
	promptText: string;
	/** Always null in PR 1. */
	personaId: string | null;
	brandKey: string;
	brandName: string;
	isTracked: boolean;
	isFocal: boolean;
	competitorId: string | null;
	category: RecommendationRowCategory;
	bestFor: string[];
	caveats: RecommendationCaveat[];
	/** Verbatim quote from the answer; null when not mentioned or unclassified. */
	evidence: string | null;
	/** The evidence was found verbatim in the answer text. */
	evidenceVerified: boolean;
	/** One of the focal brand's own pages is cited in this answer. */
	citedFocal: boolean;
	/**
	 * The full answer text. Present only when the request asked for it
	 * (`includeAnswerText`), for the L3 CSV export (rows + full answers).
	 */
	answerText?: string;
}

/** Mirrors the lab's `RecAnswerPage`. */
export interface RecommendationAnswersPage {
	rows: RecommendationAnswerRow[];
	total: number;
	page: number;
	pageSize: number;
	pageCount: number;
	filters: RecommendationAppliedFilters;
	rowFilters: RecommendationAnswerRowFilters;
}

/* ------------------------------------------------------------------ */
/* Answer record (drawer)                                              */
/* ------------------------------------------------------------------ */

/**
 * One stored assessment as the answer record (`getAnswerRecord`) returns it,
 * with tracked/focal status re-resolved at read time.
 */
export interface RecommendationAssessment {
	executionId: string;
	brandKey: string;
	/** The name as stored (the judge's spelling, or the tracked name). */
	mentionName: string;
	/** Display name: the tracked brand's canonical name when it resolves to one. */
	brandName: string;
	isFocal: boolean;
	isTracked: boolean;
	competitorId: string | null;
	/** null = unclassified (tracked brand the judge left unrated). */
	category: RecommendationCategory | null;
	bestFor: string[];
	caveats: RecommendationCaveat[];
	evidence: string | null;
	evidenceVerified: boolean;
	model: string;
	version: number;
}

/** Per-execution judge status inside an answer record. */
export interface RecommendationAssessmentStatus {
	executionId: string;
	/** ISO time the judge ran; null = not assessed (not eligible, or pending). */
	assessedAt: string | null;
	model: string | null;
	version: number | null;
}
